import { CommonModule } from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, OnDestroy, type OnInit} from '@angular/core';
import { TicketPurchaseDTO } from 'src/app/DTO/tickets/TicketPurchaseDTO';
import { TicketsService } from '../../services/tickets.service';
import { CommunityService } from 'src/app/services/community/community.service';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { ApiService } from 'src/app/services/api.service';
import Swal from "sweetalert2";
import _default from "chart.js/dist/plugins/plugin.tooltip";
import {TranslateService} from "@ngx-translate/core";
import {API_URL, environment} from "../../../../../../../environments/environment";
import { TicketDTO } from 'src/app/DTO/tickets/TicketDTO';
import { TicketsGroupDTO } from 'src/app/DTO/tickets/TicketsGroupDTO';
import {
  readTicketGroupsFromRaw,
} from 'src/app/helpers/ticket-slot.helper';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomFieldValueDTO } from 'src/app/DTO/CustomFieldValueDTO';
import { ticketI18nKey } from 'src/app/helpers/ticket-translate.helper';

@Component({
  selector: 'app-ticket-qr',
  templateUrl: './ticket-qr.component.html',
  styleUrl: './ticket-qr.component.scss',  
})
export class TicketQrComponent implements OnInit, OnDestroy {

  public models: TicketPurchaseDTO[] = [];
  public now = new Date();
  public isLoading = true;
  public colorCorp: string;
  public loadingDelete = false;
  private ticketGroupsByTicketId = new Map<number, TicketsGroupDTO[]>();
  public communityName: string;

  public ticketKey(key: string): string {
    return ticketI18nKey(this.communityName, key);
  }
  private uuid: string;
  private interval: any;
  private printParam: string;
  private translate = inject(TranslateService);
  constructor(private ticketService: TicketsService,      
              private actRoute: ActivatedRoute,
              private api: ApiService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    if (this.interval) clearInterval(this.interval);
  }

  private init() {
    this.communityName = this.actRoute.snapshot.params['community'];
    const uuid = this.actRoute.snapshot.params['uuid'];

    this.printParam = this.actRoute.snapshot.queryParams['print'];

    this.api.getCommunityByCode(this.communityName).subscribe(responseData => {
      this.colorCorp = responseData.color1;
      this.ticketService
          .getMyTicketAnonymous(this.communityName, uuid)
          .subscribe((ticket) => {
            this.models = ticket ?? [];
            this.models.forEach(x => {
              x.deletedQrImage = API_URL + '/v2/Communities/' + this.communityName + '/Modules/Tickets/qr/' + x.uuid;
            });

            this.loadTicketGroupsForPurchases().subscribe(() => {
              this.now = DateTime.now().toJSDate();
              this.isLoading = false;
              if (this.printParam
                  && !this.isLoading
                  && this.models.length
                  && this.models.some(x => !x.isDeleted)) {
                this.print();
              } else if (this.models.length === 0) {
                Swal.fire({
                  title: 'Error',
                  text: this.translate.instant('TICKETING.notTicketWithThisUuid'),
                  icon: 'error',
                  confirmButtonText: this.translate.instant('TICKETING.close')
                });
              }
            });
          })
    })
  }

  private loadTicketGroupsForPurchases() {
    const ticketIds = [...new Set(
      this.models
        .map(model => model.ticket?.id)
        .filter((id): id is number => typeof id === 'number' && id > 0),
    )];

    if (!ticketIds.length) {
      return of(void 0);
    }

    return forkJoin(
      ticketIds.map(ticketId =>
        this.api.get<TicketDTO>(`${API_URL}/v2/Communities/${this.communityName}/Modules/Tickets/${ticketId}`).pipe(
          map(ticket => {
            const raw = ticket as unknown as Record<string, unknown>;
            const groups = ticket.ticketsGroups?.length
              ? ticket.ticketsGroups
              : readTicketGroupsFromRaw(raw);
            this.ticketGroupsByTicketId.set(ticketId, groups ?? []);
          }),
          catchError(() => {
            this.ticketGroupsByTicketId.set(ticketId, []);
            return of(void 0);
          }),
        ),
      ),
    ).pipe(map(() => void 0));
  }

  private print() {
    setTimeout(() => {
      window.print();
    }, 0)
  }
  public purchaseSessionStart(purchase: TicketPurchaseDTO): string | null {
    const ticketId = purchase.ticket?.id;
    const ticketGroups = ticketId != null ? this.ticketGroupsByTicketId.get(ticketId) : undefined;
    const purchaseRaw = purchase as unknown as Record<string, unknown>;
    const group = purchase.ticketsGroup
      ?? purchaseRaw['TicketsGroup'] as TicketsGroupDTO | undefined
      ?? ticketGroups?.find(item => item.id === (purchase.ticketsGroupId ?? purchaseRaw['TicketsGroupId']));
    const groupRaw = group as unknown as Record<string, unknown> | undefined;
    const startTime = group?.startTime ?? groupRaw?.['StartTime'] as string | undefined;
    return startTime ?? null;
  }

  public purchaseCustomFieldsByLocation(
    purchase: TicketPurchaseDTO,
    location: 'PARENT' | 'CHILDREN'
  ): CustomFieldValueDTO[] {
    return (purchase.customFieldsValues ?? []).filter(cf =>
      this.isPrintableCustomField(cf) && this.customFieldLocation(cf) === location
    );
  }

  private isPrintableCustomField(cf: CustomFieldValueDTO): boolean {
    return cf.customFieldType !== 'FILE' && !!cf.value;
  }

  private customFieldLocation(cf: CustomFieldValueDTO): 'PARENT' | 'CHILDREN' {
    const raw = cf as CustomFieldValueDTO & { fieldLocation?: string; customFieldLocation?: string };
    return raw.customFieldLocation === 'CHILDREN' || raw.fieldLocation === 'CHILDREN'
      ? 'CHILDREN'
      : 'PARENT';
  }

  CancelTicket(uuid: string){
    Swal.fire({
      reverseButtons: true,
      title: this.translate.instant('TICKETING.areYouSure'),
      text: this.translate.instant('TICKETING.areYouSureText'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: 'red',
      cancelButtonColor: '#2f4050',
      confirmButtonText: this.translate.instant('TICKETING.Confirm'),
      cancelButtonText: this.translate.instant('TICKETING.Cancel'),
      showLoaderOnConfirm: true,          // <--- hace que el botón muestre spinner
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return this.ticketService.cancelTicket(this.communityName, uuid).toPromise()
            .then(() => {
              const model = this.models.find(x => x.uuid === uuid);
              model.isDeleted = true;
              model.deletedQrImage = `${API_URL}/v2/Communities/${this.communityName}/Modules/Tickets/qr/${uuid}`;
            })
            .catch((err) => {
              Swal.showValidationMessage(
                  this.translate.instant('Error') + ': ' + err
              );
            });
      }
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'success',
          title: this.translate.instant('TICKETING.ticketCanceled'),
          text: this.translate.instant('TICKETING.ticketCanceledText'),
          confirmButtonText: this.translate.instant('TICKETING.ok'),
        });
      }
    });
  }

}
